/**
 * cbx:v1 — the versioned postMessage contract.
 *
 * Two separate channels use it:
 *  1. studio  ⇄ generated-app iframe (the `CodeBox.*` runtime bridge; spec §10.2 layer 3)
 *  2. host page ⇄ studio iframe      (the Sparked dock; DEFERRED milestone, contract fixed now)
 *
 * Every message is validated with zod on BOTH ends and origin-pinned. Never trust `event.data`
 * without parsing it through these schemas.
 */
import { z } from 'zod';

export const PROTOCOL_VERSION = 'cbx:v1' as const;

const envelope = <T extends z.ZodTypeAny, K extends string>(type: K, payload: T) =>
  z.object({
    v: z.literal(PROTOCOL_VERSION),
    type: z.literal(type),
    /** correlation id — bridge calls are request/response. */
    id: z.string().min(1).max(64),
    payload,
  });

/* ---------------------------------------------------------------------------------------------
 * Bridge: generated app → studio (requests)
 * ------------------------------------------------------------------------------------------ */

export const BridgeReadySchema = envelope('bridges-ready', z.object({}));

export const AiTextRequestSchema = envelope(
  'ai.text',
  z.object({
    prompt: z.string().min(1).max(8000),
    /** structured output (Base44 InvokeLLM parity, spec D13) — validated server-side. */
    responseJsonSchema: z.record(z.unknown()).optional(),
    maxTokens: z.number().int().min(1).max(4096).optional(),
  }),
);

export const AiImageRequestSchema = envelope(
  'ai.image',
  z.object({
    prompt: z.string().min(1).max(2000),
    aspect: z.enum(['1:1', '16:9', '9:16', '4:3']).optional(),
  }),
);

export const DataRequestSchema = envelope(
  'data.op',
  z.object({
    op: z.enum(['create', 'get', 'list', 'update', 'delete']),
    entity: z.string().min(1).max(64),
    id: z.string().max(128).optional(),
    room: z.string().max(64).optional(),
    doc: z.record(z.unknown()).optional(),
    filter: z.record(z.unknown()).optional(),
    limit: z.number().int().min(1).max(200).optional(),
  }),
);

export const RealtimeRequestSchema = envelope(
  'realtime.op',
  z.object({
    op: z.enum(['join', 'leave', 'send', 'presence']),
    room: z.string().min(1).max(64),
    message: z.record(z.unknown()).optional(),
  }),
);

export const AuthRequestSchema = envelope(
  'auth.op',
  z.object({ op: z.enum(['current-user', 'sign-in-prompt']) }),
);

export const EmailRequestSchema = envelope(
  'email.send',
  z.object({
    /** app-scoped user id only — never a raw address (spec §10.4). */
    toAppUserId: z.string().min(1).max(128),
    subject: z.string().min(1).max(200),
    body: z.string().min(1).max(10000),
  }),
);

export const AssetsRequestSchema = envelope(
  'assets.get',
  z.object({
    kind: z.enum(['atlas', 'colliders', 'model']),
    name: z.string().min(1).max(128),
  }),
);

export const RuntimeErrorSchema = envelope(
  'runtime-error',
  z.object({
    message: z.string().max(2000),
    stack: z.string().max(8000).optional(),
    source: z.string().max(500).optional(),
    line: z.number().int().optional(),
    column: z.number().int().optional(),
  }),
);

export const NavigateAttemptSchema = envelope(
  'navigate-attempt',
  z.object({ url: z.string().max(2000) }),
);

export const AppRequestSchema = z.union([
  BridgeReadySchema,
  AiTextRequestSchema,
  AiImageRequestSchema,
  DataRequestSchema,
  RealtimeRequestSchema,
  AuthRequestSchema,
  EmailRequestSchema,
  AssetsRequestSchema,
  RuntimeErrorSchema,
  NavigateAttemptSchema,
]);
export type AppRequest = z.infer<typeof AppRequestSchema>;

/* ---------------------------------------------------------------------------------------------
 * Bridge: studio → generated app (responses + pushes)
 * ------------------------------------------------------------------------------------------ */

export const BridgeResponseSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  type: z.literal('response'),
  id: z.string().min(1).max(64),
  ok: z.boolean(),
  result: z.unknown().optional(),
  error: z
    .object({
      code: z.string(),
      /** always student-safe language — never a raw provider/stack message. */
      message: z.string(),
    })
    .optional(),
});
export type BridgeResponse = z.infer<typeof BridgeResponseSchema>;

export const RealtimePushSchema = envelope(
  'realtime.push',
  z.object({
    room: z.string(),
    message: z.record(z.unknown()).optional(),
    presence: z.array(z.object({ appUserId: z.string(), name: z.string() })).optional(),
  }),
);
export type RealtimePush = z.infer<typeof RealtimePushSchema>;

/* ---------------------------------------------------------------------------------------------
 * Dock: studio ⇄ Sparked host page (DEFERRED milestone — contract frozen now so both sides agree)
 * ------------------------------------------------------------------------------------------ */

export const DockOutboundSchema = z.union([
  envelope('dock.ready', z.object({})),
  envelope('dock.progress', z.object({ step: z.number().int(), total: z.number().int() })),
  envelope('dock.completed', z.object({ score: z.number().optional(), summary: z.string().optional() })),
  envelope('dock.budget', z.object({ left: z.number(), total: z.number() })),
  envelope('dock.budget_exhausted', z.object({})),
  envelope('dock.published', z.object({ url: z.string() })),
  envelope('dock.error', z.object({ message: z.string() })),
]);
export type DockOutbound = z.infer<typeof DockOutboundSchema>;

export const DockInboundSchema = z.union([
  envelope(
    'dock.init',
    z.object({
      activityId: z.string(),
      learnerToken: z.string(),
      mode: z.enum(['build', 'play', 'review']).default('build'),
      budgetOverride: z.number().optional(),
      spec: z.record(z.unknown()).optional(),
      seed: z.record(z.unknown()).optional(),
    }),
  ),
  envelope(
    'dock.inject',
    z.object({
      kind: z.enum(['starter', 'data', 'coach-hint']),
      payload: z.record(z.unknown()),
    }),
  ),
]);
export type DockInbound = z.infer<typeof DockInboundSchema>;

/* ---------------------------------------------------------------------------------------------
 * WS: box-api → studio (job streaming; spec D5)
 * ------------------------------------------------------------------------------------------ */

/** P1 task list (SPEC-P1-TASKLIST §4) — mirrors the JobTaskStatus enum in box-api's Prisma schema. */
export const JobTaskStatusSchema = z.enum(['planned', 'running', 'done', 'failed', 'skipped']);
export type JobTaskStatus = z.infer<typeof JobTaskStatusSchema>;

/**
 * The coarse build stages, in order. The stepper renders exactly these.
 *
 * They live on the TASK because the client cannot infer them: it used to derive the stage from the
 * job phase plus "has a checklist arrived", which let the stepper show "Building" while the running
 * task was literally "Plan the build".
 */
export const BuildStageSchema = z.enum(['plan', 'build', 'review', 'finish']);
export type BuildStage = z.infer<typeof BuildStageSchema>;

export const JobTaskSchema = z.object({
  id: z.string(),
  order: z.number(),
  title: z.string(),
  detail: z.string().nullable().optional(),
  stage: BuildStageSchema.default('build'),
  status: JobTaskStatusSchema,
});
export type JobTaskView = z.infer<typeof JobTaskSchema>;

/**
 * A job the client did not start, announced the moment it exists.
 *
 * Without this the studio has no way to learn about server-created work — auto-fixes, care checks,
 * feedback jobs — because every other event in this union identifies a job the client is assumed to
 * already know. That gap is the sole reason the studio polled `/jobs` on a 1.5s timer. The shape
 * mirrors the `/jobs` row so the rail can render it without a follow-up fetch.
 */
export const JobCreatedSchema = z.object({
  type: z.literal('job.created'),
  jobId: z.string(),
  jobType: z.string(),
  status: z.enum(['queued', 'running', 'done', 'error', 'cancelled']),
  phase: z.string().nullable().optional(),
  createdAt: z.string(),
  /** only the fields the rail reads — never the whole payload, which can carry prompts and errors */
  payload: z
    .object({ mode: z.string().optional(), autofix: z.boolean().optional(), error: z.object({ kind: z.string().optional() }).optional() })
    .optional(),
});

/**
 * Per-job monotonic counter stamped on every job frame (WS2).
 *
 * A push stream with no numbering cannot tell "nothing happened" from "I missed something", so a
 * dropped frame left the UI silently wrong until a timer happened to correct it. With `seq` the
 * client detects `seq > lastSeq + 1` and reconciles immediately — a gap becomes a sub-second
 * self-heal instead of a frozen screen, and frame loss becomes countable rather than inferred.
 */
export const JobEventSchema = z.discriminatedUnion('type', [
  JobCreatedSchema,
  z.object({
    type: z.literal('job.phase'),
    jobId: z.string(),
    phase: z.string(),
    /** student-language narration (Sparky voice), not the raw phase name. */
    narration: z.string().optional(),
  }),
  z.object({ type: z.literal('job.token'), jobId: z.string(), text: z.string() }),
  z.object({
    type: z.literal('job.done'),
    jobId: z.string(),
    versionId: z.string().optional(),
    /** what the run cost — drives the build-summary screen without a follow-up fetch */
    usage: z.object({ inputTokens: z.number(), outputTokens: z.number() }).optional(),
  }),
  z.object({ type: z.literal('job.error'), jobId: z.string(), message: z.string() }),
  z.object({ type: z.literal('job.cancelled'), jobId: z.string() }),
  /** full list — sent on seed and whenever the set changes wholesale (re-plan, fail-open) */
  z.object({ type: z.literal('job.tasks'), jobId: z.string(), tasks: z.array(JobTaskSchema) }),
  /** single-row delta — the hot path while a job runs */
  z.object({
    type: z.literal('task.update'),
    jobId: z.string(),
    taskId: z.string(),
    status: JobTaskStatusSchema,
    /** optional Sparky line for the "now doing" strip; defaults to the task title client-side */
    narration: z.string().optional(),
  }),
]);
export type JobEvent = z.infer<typeof JobEventSchema>;

/** A job frame on the wire: the event plus its per-job sequence number. */
export type SequencedJobEvent = JobEvent & { seq?: number };
